/**
 * The one place the FE sends bytes to S3.
 *
 * Every upload hook routes its PUT through here, for one reason that is
 * worth stating plainly because it is not obvious and it decides the whole
 * shape of the upload layer:
 *
 *   **`fetch()` has NO upload-progress event.** There is no option to pass
 *   and no callback to attach. While a transfer runs through `fetch`, a
 *   percentage is simply unobtainable — which is why nothing in this app
 *   reported upload progress before this module existed.
 *
 * `XMLHttpRequest` still exposes `upload.onprogress`, and it throws in
 * `abort()` for free, so progress and cancellation arrive in the same
 * change. The alternative — `fetch` with a `ReadableStream` body and
 * `duplex: 'half'` — is Chrome-only and Safari does not implement it, so
 * it is not a real option. Do not "modernise" this back to `fetch`.
 *
 * This module deliberately stays dumb: no retries, no state, no
 * throttling, no knowledge of presign or attach. It sends one request and
 * reports what happened. Everything policy-shaped lives in the hooks.
 */

/** Bytes sent versus bytes to send, for one in-flight transfer. */
export interface PutProgress {
  loaded: number
  total: number
}

export interface PutWithProgressOptions {
  /** The presigned URL. Already carries the signature and its expiry. */
  url: string
  file: File
  /**
   * The presign response's `requiredHeaders`, replayed VERBATIM.
   *
   * They are part of what was signed — `Content-Type` plus either
   * `x-amz-server-side-encryption: aws:kms` (tenant bucket, whose policy
   * denies unencrypted PUTs) or `Cache-Control` (public assets). Drop one,
   * rename one, or add one of your own and S3 answers 403
   * SignatureDoesNotMatch. In particular never attach this app's own
   * `Authorization` / `X-Tenant-Subdomain` headers here: S3 is not our API
   * and those would invalidate the signature.
   */
  headers: Record<string, string>
  signal?: AbortSignal
  /**
   * Called as bytes go out. Fires FREQUENTLY — many times a second on a
   * fast link — so the consumer is responsible for coalescing before it
   * touches React state. This module reports every event it is given.
   */
  onProgress?: (progress: PutProgress) => void
}

export interface PutWithProgressResult {
  status: number
  statusText: string
  /**
   * `x-amz-version-id`. The wiki file-node flow needs it to complete its
   * attach step.
   *
   * Null here is worth distinguishing from "the browser hid it": reading a
   * response header cross-origin requires the bucket's CORS
   * `ExposeHeaders` to list it, and the uploads bucket does list this one
   * (set durably in CDK). So a null on staging points at bucket config,
   * not at this code.
   */
  versionId: string | null
  /** Response body, used to build the error message on a non-2xx. */
  responseText: string
}

/**
 * PUT a file to a presigned URL, reporting progress and honouring an
 * `AbortSignal`.
 *
 * Resolves for ANY completed response, including 4xx and 5xx — the caller
 * inspects `status`, exactly as it used to inspect `response.ok`. It
 * rejects only when no response was obtained at all: a transport failure
 * or an abort.
 */
export function putWithProgress(options: PutWithProgressOptions): Promise<PutWithProgressResult> {
  const { url, file, headers, signal, onProgress } = options

  return new Promise<PutWithProgressResult>((resolve, reject) => {
    // Honour a signal that is already aborted before we open a socket.
    // Without this an upload cancelled during presign would still send its
    // bytes, and the caller's cleanup would race a transfer it believed
    // was never started.
    if (signal?.aborted) {
      reject(abortError())
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)

    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value)
    }

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        // `event.total` is 0 whenever the length is not computable. For a
        // plain PUT of a File it always is, but taking the total from
        // `file.size` means a consumer can never divide by zero, and it
        // stays correct if a future caller sends something exotic.
        onProgress({ loaded: event.loaded, total: file.size })
      }
    }

    let detachAbortListener: (() => void) | undefined

    const settle = () => {
      detachAbortListener?.()
    }

    // Fires for every completed response, success or error status alike.
    xhr.onload = () => {
      settle()
      resolve({
        status: xhr.status,
        statusText: xhr.statusText,
        versionId: xhr.getResponseHeader('x-amz-version-id'),
        responseText: xhr.responseText,
      })
    }

    // The two rejection paths deliberately mirror the shapes `fetch`
    // produces, because callers already branch on them: a transport
    // failure is a `TypeError`, and an abort is an error NAMED
    // 'AbortError'. `useMultiImageUpload` tests that name to tell a user
    // cancellation apart from a real failure, so changing it would
    // silently convert cancellations into error states.
    xhr.onerror = () => {
      settle()
      reject(new TypeError('Network request failed'))
    }

    // `ontimeout` can only fire if a timeout is actually set, and it is
    // deliberately NOT set: a large upload on a slow link is the normal
    // case here, so any fixed ceiling would cancel legitimate transfers.
    // The handler stays because a socket that dies without a response
    // otherwise resolves nothing and leaves a registry entry — and with
    // it the reload prompt — alive for the life of the tab.
    xhr.ontimeout = () => {
      settle()
      reject(new TypeError('Network request timed out'))
    }

    xhr.onabort = () => {
      settle()
      reject(abortError())
    }

    if (signal) {
      const onAbort = () => xhr.abort()
      signal.addEventListener('abort', onAbort, { once: true })
      // Removing the listener once the request settles keeps a long-lived
      // signal (one shared across a batch, say) from retaining a finished
      // request's closure for as long as the signal lives.
      detachAbortListener = () => signal.removeEventListener('abort', onAbort)
    }

    xhr.send(file)
  })
}

/**
 * `DOMException` is available in every browser and in jsdom, but building
 * the error in one place keeps the name consistent — and the name is the
 * part callers actually branch on.
 */
function abortError(): Error {
  return new DOMException('The operation was aborted.', 'AbortError')
}

/**
 * True when a thrown value is a user-driven cancellation rather than a
 * failure.
 *
 * Duck-typed on the NAME, and deliberately NOT gated behind
 * `instanceof Error`: a `DOMException` is not an `instanceof Error` in
 * jsdom (nor across realms in a browser), so an `Error`-gated check
 * returns false for the exact case this exists to catch, and every
 * cancellation would be misreported as a failure. The pre-existing check
 * in `useMultiImageUpload` tests `DOMException` separately for the same
 * reason; this helper subsumes it.
 */
export function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  )
}
